import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/retos/")({
  head: () => ({
    meta: [
      { title: "Mis Retos — Watts" },
      { name: "description", content: "Conquista Catalunya puerto a puerto con los retos de Watts." },
      { property: "og:title", content: "Mis Retos — Watts" },
      { property: "og:description", content: "Conquista Catalunya puerto a puerto con los retos de Watts." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: RetosIndex,
});

interface Challenge {
  id: string;
  name: string;
  description: string;
  emoji: string;
  is_featured: boolean;
  sort_order: number;
}

interface ChallengePort {
  challenge_id: string;
  port_id: string;
}

interface Completion {
  port_id: string;
  status: string;
}

interface Port {
  id: string;
}

const blockTexture =
  "repeating-linear-gradient(125deg, rgba(255,255,255,0.014) 0px, rgba(255,255,255,0.014) 1px, transparent 1px, transparent 6px)";

export default function RetosIndex() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [challengePorts, setChallengePorts] = useState<ChallengePort[]>([]);
  const [completions, setCompletions] = useState<Completion[]>([]);
  const [totalPorts, setTotalPorts] = useState(0);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      navigate({ to: "/auth" });
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;

    const load = async () => {
      setBusy(true);
      try {
        const [{ data: ch }, { data: cp }, { data: pc }, { data: portsData }] = await Promise.all([
          supabase.from("challenges").select("id, name, description, emoji, is_featured, sort_order").order("sort_order"),
          supabase.from("challenge_ports").select("challenge_id, port_id"),
          supabase.from("port_completions").select("port_id, status").eq("user_id", user.id).eq("status", "approved"),
          supabase.from("ports").select("id"),
        ]);
        setChallenges((ch as Challenge[]) ?? []);
        setChallengePorts((cp as ChallengePort[]) ?? []);
        setCompletions((pc as Completion[]) ?? []);
        setTotalPorts((portsData as Port[] ?? []).length);
      } finally {
        setBusy(false);
      }
    };

    load();
  }, [user?.id]);

  const progress = (challengeId: string) => {
    const ports = challengePorts.filter((p) => p.challenge_id === challengeId).map((p) => p.port_id);
    const total = ports.length;
    const done = completions.filter((c) => ports.includes(c.port_id)).length;
    return { total, done, percent: total > 0 ? Math.round((done / total) * 100) : 0 };
  };

  const globalProgress = () => {
    const total = totalPorts;
    const done = total > 0 ? completions.length : 0;
    return { total, done, percent: total > 0 ? Math.round((done / total) * 100) : 0 };
  };

  const orderedChallenges = challenges.sort((a, b) => a.sort_order - b.sort_order);
  const gp = globalProgress();

  return (
    <div className="h-screen overflow-y-auto bg-background fade-in pb-24">
      <div className="mx-auto w-full md:max-w-[1100px]">
        <header className="px-5 pt-[max(env(safe-area-inset-top),1rem)] pb-6">
          <h1
            className="font-display text-[40px] font-black uppercase leading-[0.92] tracking-tight text-white"
            style={{ fontWeight: 900 }}
          >
            CONQUISTA
            <br />
            <span className="text-[#F5C518]">CATALUNYA</span>
          </h1>
          <p className="mt-3 text-xs text-[#4a4a4a]">15 puertos · 3 retos · 1 objetivo</p>
        </header>

        <main className="space-y-6 px-4">
        {busy && (
          <div className="space-y-6">
            <div className="h-20 animate-pulse rounded-2xl bg-surface" />
            <div className="grid grid-cols-2 gap-[11px]">
              <div className="aspect-[3/4] animate-pulse rounded-2xl bg-surface" />
              <div className="aspect-[3/4] animate-pulse rounded-2xl bg-surface" />
              <div className="aspect-[3/4] animate-pulse rounded-2xl bg-surface" />
              <div className="aspect-[3/4] animate-pulse rounded-2xl bg-surface" />
            </div>
            <div className="h-24 animate-pulse rounded-2xl bg-surface" />
          </div>
        )}

        {!busy && (
          <>
            {/* Global progress card */}
            <div className="flex items-center gap-5 rounded-2xl border border-[#2a2a2a] bg-[#1a1a1a] p-4">
              <div className="flex items-baseline gap-0.5 leading-none">
                <span className="font-display text-[40px] font-black text-[#F5C518]" style={{ fontWeight: 900 }}>
                  {gp.done}
                </span>
                <span className="font-display text-[40px] font-black text-[#4a4a4a]" style={{ fontWeight: 900 }}>
                  /{gp.total}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold tracking-[0.12em] text-[#4a4a4a] uppercase">PROGRESO TOTAL</p>
                <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-[#2a2a2a]">
                  <div className="h-full bg-[#F5C518] transition-all duration-500" style={{ width: `${gp.percent}%` }} />
                </div>
              </div>
            </div>

            {/* Challenges grid */}
            <div className="grid grid-cols-2 gap-[11px] md:grid-cols-3 lg:grid-cols-4">
              {orderedChallenges.map((c, index) => {
                const p = progress(c.id);
                const [firstWord, ...restWords] = c.name.split(" ");
                const isFeatured = c.is_featured;

                return (
                  <Link
                    key={c.id}
                    to="/retos/$id"
                    params={{ id: c.id }}
                    className="press-scale relative flex flex-col overflow-hidden rounded-[16px] border border-[#2a2a2a] bg-[#1a1a1a] p-3 max-h-[300px]"
                    style={{
                      aspectRatio: "3/4",
                      backgroundImage: isFeatured
                        ? `radial-gradient(ellipse at top, rgba(245,197,24,0.17) 0%, transparent 60%), ${blockTexture}`
                        : blockTexture,
                      borderColor: isFeatured ? "#3a3320" : "#2a2a2a",
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <span
                        className={`font-display text-[11px] font-black uppercase tracking-[2px] ${
                          isFeatured ? "text-[#F5C518]" : "text-[#4a4a4a]"
                        }`}
                        style={{ fontWeight: 900 }}
                      >
                        RETO {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="text-xl leading-none">{c.emoji}</span>
                    </div>

                    <div className="mt-auto">
                      <div className="font-display text-[25px] font-black uppercase leading-[0.9]" style={{ fontWeight: 900 }}>
                        <span className="text-white">{firstWord}</span>
                        <br />
                        <span className="text-[#F5C518]">{restWords.join(" ")}</span>
                      </div>
                      <p className="mt-2 line-clamp-2 text-[10.5px] leading-tight text-[#4a4a4a]">{c.description}</p>

                      <div className="mt-3">
                        <div className="flex items-end justify-between">
                          <span
                            className="font-display text-[23px] font-black text-[#F5C518]"
                            style={{ fontWeight: 900 }}
                          >
                            {p.percent}%
                          </span>
                          <span className="text-[10px] text-[#4a4a4a]">
                            {p.done}/{p.total}
                          </span>
                        </div>
                        <div className="mt-1.5 h-[3px] w-full overflow-hidden rounded-full bg-[#2a2a2a]">
                          <div
                            className="h-full bg-[#F5C518] transition-all duration-500"
                            style={{ width: `${p.percent}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}

              {/* Locked block */}
              <div
                className="relative flex flex-col overflow-hidden rounded-[16px] border border-[#2a2a2a] bg-[#1a1a1a] p-3 max-h-[300px]"
                style={{ aspectRatio: "3/4", opacity: 0.45, backgroundImage: blockTexture }}
              >
                <div className="flex items-start justify-between">
                  <span
                    className="font-display text-[11px] font-black uppercase tracking-[2px] text-[#4a4a4a]"
                    style={{ fontWeight: 900 }}
                  >
                    PRÓXIMAMENTE
                  </span>
                  <span className="text-xl leading-none">🔒</span>
                </div>

                <div className="mt-auto">
                  <div className="font-display text-[25px] font-black uppercase leading-[0.9] text-[#4a4a4a]" style={{ fontWeight: 900 }}>
                    WATTS
                    <br />
                    50
                  </div>
                  <p className="mt-2 line-clamp-2 text-[10.5px] leading-tight text-[#4a4a4a]">
                    El gran reto. Los 50 puertos de Catalunya
                  </p>

                  <div className="mt-3">
                    <div className="flex items-end justify-between">
                      <span
                        className="font-display text-[23px] font-black text-[#4a4a4a]"
                        style={{ fontWeight: 900 }}
                      >
                        0%
                      </span>
                      <span className="text-[10px] text-[#4a4a4a]">0/50</span>
                    </div>
                    <div className="mt-1.5 h-[3px] w-full overflow-hidden rounded-full bg-[#2a2a2a]">
                      <div className="h-full bg-[#4a4a4a]" style={{ width: "0%" }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Province explorer */}
            <div className="rounded-2xl border border-[#2a2a2a] bg-[#1a1a1a] p-4">
              <h3 className="font-display text-[16px] font-bold uppercase tracking-wide text-white">EXPLORA POR PROVINCIA</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {["Barcelona", "Girona", "Lleida", "Tarragona"].map((prov) => (
                  <Link
                    key={prov}
                    to="/retos/provincias"
                    className="rounded-full border border-[#2a2a2a] px-3 py-1.5 text-[11px] text-[#4a4a4a] transition-colors hover:border-[#F5C518] hover:text-[#F5C518]"
                  >
                    {prov}
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
      </div>
    </div>
  );
}
